<template>
  <span class="tour-popover-container">
    <v-popover
      v-if="shouldShow"
      :popover-class="'tour-popover ' + tourtipName"
      :offset="offset"
      :auto-hide="autoHide"
      :trigger="trigger"
      :placement="placement"
      :open="open"
      :container="'#root .router-view'"
    >

      <slot />

      <template v-slot:popover>
        <p
          v-if="header != ''"
          class="tourtip-header"
        >{{ header }}</p>
        <p
          v-if="message != ''"
          class="tourtip-body"
        >{{ message }}</p>
        <button
          v-if="closeText != ''"
          class="button tourtip-close"
          @click="close"
        >
          {{ closeText }}
        </button>
      </template>
    </v-popover>
    <span v-else>
      <slot />
    </span>
  </span>
</template>

<script>
let tourData = {
  correctness: {
    message: 'Read the explanations to grasp the underlying concept of each question.',
    header: 'Now it\'s time to learn.',
    closeText: 'Got It!',
    name: 'correctness',
    placement: 'bottom',
  },
  progress: {
    message: 'You\'ve answered questions, tagged your confidence level and learned from explanations. Keep going!',
    header: 'You\'re almost there',
    closeText: 'Got It!',
    name: 'progress',
    placement: 'bottom',
  },
  filters: {
    message: 'Tap a tag to review questions. Master the red "Don\'t Know" questions first.',
    header: 'Go after the weak spots.',
    closeText: '',
    name: 'filters',
    placement: 'bottom',
  },
  tagging: {
    message: 'Choose a tag based on your confidence level. Click the best option and you\'ll move to the next question. We\'ll come back to tags later!',
    header: 'Did you know the answer?',
    closeText: 'Got It!',
    name: 'tagging',
    placement: 'top',
  },
};

export default {
  props: {
    autoHide: {
      type: Boolean,
      default: false,
    },
    offset: {
      type: Number,
      default: 0,
    },
    trigger: {
      type: String,
      default: '',
    },
    tourtipName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loaded: false,
      open: true,
      message: '',
      placement: '',
      header: '',
      closeText: '',
    };
  },
  computed: {
    numFlashcards() {
      const stateList = this.$store.getters['flashcardStates/flashcardStateList'];
      return stateList.length;
    },
    shouldShow() {
      let onTour = this.$store.getters['session/onTour'];
      if (!this.loaded || !onTour) {
        return false;
      }

      switch (true) {
        case this.tourtipName === 'tagging':
          return this.tourSequence === 1;
        case this.tourtipName === 'correctness':
          return this.tourSequence === 2;
        case this.tourtipName === 'progress':
          return this.tourSequence === 3;
        case this.tourtipName === 'filters':
          return onTour; // If a user is on tour they should see the filters tourtip all the time
        default:
          return false;
      }
    },
    tourSequence() {
      return this.$store.getters['session/tourSequence'];
    },
  },
  created() {
    this.loaded = true;
    let tourDatum = tourData[this.tourtipName];
    if (tourDatum) {
      this.message = tourDatum.message;
      this.placement = tourDatum.placement;
      this.header = tourDatum.header;
      this.closeText = tourDatum.closeText;
    }
    this.$watch('numFlashcards', (newValue, oldValue) => {
      if (newValue > oldValue) {
        this.$store.dispatch('session/incrementTourSequence');
      }
    });
  },
  methods: {
    close() {
      this.open = false;
    },
  },
};
</script>

<style lang='scss'>
  // overrides some styles generated by JS
  .tour-popover-container span.trigger {
    display: block !important;
  }

  .tour-popover {
    line-height: 1.25;
    .tourtip-header {
      display: block;
      font-weight: $bold;
      margin-top: rem-calc(8);
    }
    .tourtip-body {
      display: block;
    }
    .tourtip-close {
      background-color: #00d8f8;
      border: 0;
      border-radius: 24px;
      color: #ffffff;
      display: block;
      margin-top: rem-calc(16);
      margin-left: auto;
      padding: rem-calc(10) rem-calc(28);
      font-weight: $semi-bold;
    }

    &.tooltip {
      border-radius: rem-calc(8);
      box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.4);
      z-index: 10000;

      &[x-placement^="top"] {
        .tooltip-arrow {
          border-width: 5px 5px 0 5px;
          border-left-color: transparent !important;
          border-right-color: transparent !important;
          border-bottom-color: transparent !important;
          bottom: -5px;
        }
      }

      &.tagging {
        &[x-placement^="top"] {
          margin-bottom: rem-calc(14);
          margin-left: rem-calc(40);

          .tooltip-arrow {
            left: calc(50% - 10px) !important;
          }
        }
      }

      &[x-placement^="bottom"] {
        .tooltip-arrow {
          border-width: 0 5px 5px 5px;
          border-left-color: transparent !important;
          border-right-color: transparent !important;
          border-top-color: transparent !important;
          top: -5px;
        }
      }

      &.filters {
        &[x-placement^="bottom"] {
          margin: rem-calc(16) 0 0 rem-calc(120);

          @include breakpoint(small) {
            margin: rem-calc(16) 0 0 rem-calc(42);
          }

          .tooltip-arrow {
            left: rem-calc(54) !important;
          }
        }
      }

      &.progress {
        @include breakpoint(small) {
          margin-left: rem-calc(-16);
        }

        &[x-placement^="bottom"] {
          margin-top: rem-calc(-56);
        }
      }

      &.correctness {
        &[x-placement^="bottom"] {
          margin-top: rem-calc(16);
          margin-left: rem-calc(40);

          @include breakpoint(small) {
            margin-left: rem-calc(6);
          }

          .tooltip-arrow {
            left: 8px !important;
          }
        }
      }

      &.popover {
        $color: #001c74;

        .popover-inner {
          background: $color;
          color: #ffffff;
          padding: rem-calc(16) rem-calc(24);
          border-radius: rem-calc(8);
          box-shadow: 0 5px 30px rgba(black, .1);
        }

        .popover-arrow {
          border-color: $color;
        }
      }

      &[aria-hidden='true'] {
        visibility: hidden;
        opacity: 0;
        transition: opacity .15s, visibility .15s;
      }

      &[aria-hidden='false'] {
        visibility: visible;
        opacity: 1;
        transition: opacity .15s;
      }
    }
  }
</style>
